/**
 * The three deterministic fingerprints Phase 1 guards mesh edits with.
 *
 * They are three, not one, because they answer three different questions and a
 * single hash cannot answer any two of them without giving a wrong answer to
 * the other:
 *
 *   rawTopologyFingerprint  "do these ids still mean the same elements?"
 *                           Position-INDEPENDENT. Survives a whole sculpt pass.
 *   cornerFingerprint       "do these corner groups still hold?"
 *                           Position-DEPENDENT, because corner membership is
 *                           derived from exact position coincidence.
 *   objectHash              "is this object still in the state I last saw?"
 *                           Everything restoreObjectSnapshot can overwrite.
 *
 * A caller reusing a resolved selection across several of its own edits needs
 * the first two to be stable under a pure position change and to break under a
 * topology change. A caller about to restore a snapshot needs the third to
 * break under *any* change, including one that moved nothing but positions.
 * Collapsing them would force one of those two callers to be wrong.
 *
 * All three hash a `canonicalJson` string, which sorts object keys at every
 * depth and rounds numbers through `canonicalNumber` — the repository's
 * existing determinism primitive, already relied on by scene serialization.
 * `crypto.subtle` is used because it is available identically in the browser,
 * in Node, and in Workers, which is what lets a fingerprint computed by a
 * viewer be compared against one computed anywhere else.
 */

import { canonicalJson, canonicalNumber, canonicalRotation } from '../canonical-json.js';
import { normalizeShading } from './shading.js';
import { computeGeometricAdjacency } from './adjacency.js';
import { FACE_NOT_RENDERABLE } from './validation.js';

const encoder = new TextEncoder();

async function sha256(canonical) {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(canonical));
  return `sha256:${[...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')}`;
}

function byFirstElement(a, b) {
  return a[0] < b[0] ? -1 : (a[0] > b[0] ? 1 : 0);
}

/**
 * Rotate a face's vertex ids so the smallest leads, preserving cyclic order.
 *
 * `[a,b,c]`, `[b,c,a]` and `[c,a,b]` are the SAME face wound the same way —
 * they differ only in which corner the array happens to start at. Hashing them
 * differently would invent a change where there is none: `orientFacesOutside`
 * rewinding a face returns the correct winding but not necessarily the original
 * starting vertex, and a caller's resolved selection would be invalidated by a
 * no-op. Rotation is normalised away; REVERSAL is not, because `[a,b,c]`
 * normalises to `[a,b,c]` while its reverse `[c,b,a]` normalises to `[a,c,b]`.
 * So winding stays part of the identity and starting corner does not.
 */
function canonicalFaceCycle(vertexIds) {
  let lead = 0;
  for (let i = 1; i < vertexIds.length; i++) {
    if (vertexIds[i] < vertexIds[lead]) lead = i;
  }
  return [...vertexIds.slice(lead), ...vertexIds.slice(0, lead)];
}

/**
 * Identity of the topology's ADDRESS SPACE: which ids exist, which vertices
 * each face joins, in what order, and where the id counters stand.
 *
 * Face vertex ids are kept in their authored ORDER, never sorted. Winding is
 * part of a face's identity: `recalculateOutside` reverses vertex order without
 * changing a single id, and this fingerprint has to notice. A face-id-to-
 * sorted-vertex-id hash would collide across that flip and is explicitly not
 * sufficient.
 *
 * `nextVertexId` / `nextFaceId` are included. They are part of persisted,
 * parsed topology (`parseTopology` normalises both), they determine every id
 * minted from here on, and `restoreObjectSnapshot` puts them back. Two states
 * with identical vertices and faces but different counters must not hash
 * identically, or a restore could rewind a counter and a later operation would
 * mint an id that collides with one already in use.
 *
 * Deliberately excludes positions, so a pure `moveCorners` — or a whole series
 * of them — leaves this unchanged and a resolved id list stays reusable.
 */
export async function rawTopologyFingerprint(topology) {
  return sha256(canonicalJson({
    v: topology.vertices.map((vertex) => vertex.id).sort(),
    f: topology.faces
      .map((face) => [face.id, ...canonicalFaceCycle(face.vertexIds)])
      .sort(byFirstElement),
    e: [...(topology.explicitEdgeIds ?? [])].sort(),
    nv: topology.nextVertexId,
    nf: topology.nextFaceId,
  }));
}

/**
 * Identity of the derived CORNER GROUPING — the only adjacency-derived thing
 * `moveCorners` addresses by.
 *
 * Membership only. `edgeIds`, `regionIds`, `faceIds` and `position` are
 * deliberately excluded: region classification is recomputed from face normals,
 * so an ordinary welded displacement reshuffles it constantly. Measured on a
 * box, moving one corner takes regions 6 to 7, geometric edges 12 to 13 and
 * diagonals 6 to 5 while corner membership does not move at all. Including any
 * of that would make every second edit in a pass fail its own guard for no
 * safety benefit — `moveCorners` never reads those fields. What it does read,
 * corner to member vertex ids, is exactly what is hashed.
 *
 * Returns `{ value, error }` rather than throwing. `computeGeometricAdjacency`
 * refuses a mesh containing a degenerate face (see the degenerate-face audit),
 * and a *read* that reports fingerprints alongside other findings must still be
 * able to return the rest of them. A caller that needs the value must handle
 * `value === null`; `moveCorners` treats it as a mesh it will not edit.
 */
export async function cornerFingerprint(topology) {
  let corners;
  try {
    ({ corners } = computeGeometricAdjacency(topology));
  } catch (error) {
    if (error?.code === FACE_NOT_RENDERABLE) return { value: null, error: FACE_NOT_RENDERABLE };
    throw error;
  }
  const value = await sha256(canonicalJson({
    c: corners.map((corner) => [corner.id, [...corner.vertexIds].sort()]).sort(byFirstElement),
  }));
  return { value, error: null };
}

/**
 * Identity of the object's full restorable state.
 *
 * Takes a plain descriptor rather than a `THREE.Object3D` so this module stays
 * free of a three dependency and so the hashed shape is literally the snapshot
 * payload plus `parent` — which makes "the hash covers everything a restore can
 * overwrite" checkable by reading one object literal instead of tracing field
 * access through the registry.
 *
 * `parent` is hashed but is NOT restorable in Phase 1. That asymmetry is
 * deliberate: the hash covering more than the restore scope means a reparent by
 * another actor causes a conservative rejection, which is the safe direction.
 * If parenting later becomes restorable, this field is already accounted for.
 *
 * Positions live here rather than in `rawTopologyFingerprint` precisely because
 * a restore must notice an intervening edit that moved nothing else.
 */
export async function objectHash({
  topology,
  transform,
  parent = null,
  name = null,
  material = null,
  // Shading is part of the state this hash guards: two objects with identical
  // topology and transform that render differently are not interchangeable,
  // and a restore that could not see the difference would silently discard it.
  // Omitted means flat, so every object created before Milestone D hashes as
  // what it actually was.
  shading = null,
}) {
  if (!topology || !Array.isArray(topology.vertices)) {
    throw new Error('objectHash requires a topology with a vertices array');
  }
  if (!transform || !Array.isArray(transform.position)) {
    throw new Error('objectHash requires a transform with a position array');
  }
  return sha256(canonicalJson({
    raw: await rawTopologyFingerprint(topology),
    p: topology.vertices
      .map((vertex) => [vertex.id, ...vertex.position.map(canonicalNumber)])
      .sort(byFirstElement),
    t: {
      position: transform.position.map(canonicalNumber),
      rotationDeg: canonicalRotation(transform.rotationDeg ?? [0, 0, 0]),
      scale: (transform.scale ?? [1, 1, 1]).map(canonicalNumber),
    },
    parent,
    name,
    material: material === null ? null : {
      color: material.color ?? null,
      roughness: material.roughness ?? null,
      metalness: material.metalness ?? null,
    },
    shading: normalizeShading(shading),
  }));
}
